/**
 * @file numberUtils.js
 * @description This file contains all the utilities regarding number operations
 * @Author: Saurabh Dohaiya
 */

export const roundToDecimals = (value, decimals = 2) => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

/**
 * Turn any “amount‑like” string into a number string
 * with a chosen number of decimal places.
 *
 * @param  {string}  rawNum        e.g. "1,234.5", " 99 ", "42"
 * @param  {number}  precision  how many digits after the decimal (default = 2)
 * @return {string}             e.g. "1234.50", "99.000"
 */
export const toFixedNumber = (rawNum, precision = 2) => {
  if (typeof rawNum !== 'string') return (0).toFixed(precision);

  const sanitizedNum = rawNum.replace(/,/g, '').trim();

  const num = parseFloat(sanitizedNum);
  if (isNaN(num)) return (0).toFixed(precision);

  return num.toFixed(precision);
}

/**
 * Format a number to a maximum of 2 decimal places, removing trailing zeros.
 * Useful for displaying amounts in a clean, readable format.
 *
 * @param  {number|string|null|undefined}  value  The value to format
 * @return {number|string}                        Formatted number or empty string if invalid
 *
 * @example
 * formatAmount(100.123)  // returns 100.12
 * formatAmount(50.5)     // returns 50.5
 * formatAmount(1000)     // returns 1000
 * formatAmount(10.00)    // returns 10
 * formatAmount(null)     // returns ''
 */
export const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  // Format to 2 decimal places and convert back to number to remove trailing zeros
  return parseFloat(num.toFixed(2));
};

/**
 * Format a number as a localized currency string for display.
 * Falls back gracefully when the user is still typing or when the currency
 * code isn't a known ISO 4217 value.
 *
 * Differs from `formatAmount` above: this returns a *display string*
 * with thousands separators and currency symbol, not a numeric value.
 *
 * @param  {number|string|null|undefined}  rawAmount  amount to format
 * @param  {string}                        currency   ISO 4217 currency code
 *                                                    (e.g. "GBP"); optional
 * @return {string|null}                              localized string,
 *                                                    or null if rawAmount
 *                                                    is empty / invalid
 *
 * @example
 * formatCurrencyAmount(1234.5, 'GBP')   // "£1,234.50"
 * formatCurrencyAmount(1234.5, 'USD')   // "$1,234.50"
 * formatCurrencyAmount(1234.5)          // "1,234.5"
 * formatCurrencyAmount(1234.5, 'XYZ')   // "1,234.5 XYZ"  (unknown code)
 * formatCurrencyAmount('', 'GBP')       // null
 */
export const formatCurrencyAmount = (rawAmount, currency) => {
  if (rawAmount === '' || rawAmount === null || rawAmount === undefined) {
    return null;
  }
  const numeric = Number(rawAmount);
  if (!Number.isFinite(numeric)) return null;
  if (!currency) {
    return numeric.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2
    }).format(numeric);
  } catch {
    return `${numeric.toLocaleString('en-US')} ${currency}`;
  }
};